import React from 'react';
import { Button, Col, DatePicker, Form, Input, message, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { AkamLogoCheckIcon, AkamLogoTextIcon, ArrowDownIcon, CalendarIcon } from 'icons';
import { useTranslations } from 'next-intl';

import { useAppDispatch } from 'store';

import { closeAuthModal, IProfile, useRegister } from 'modules/auth';
import { useGetRegions } from 'modules/global';

import { IUserData } from '.';

interface IProps {
  data: IUserData;
  setData: React.Dispatch<React.SetStateAction<IUserData>>;
}

const { useForm } = Form;

const RegisterStep: React.FC<IProps> = () => {
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const t = useTranslations();
  const { regions, isLoading: regionsLoading } = useGetRegions({ isNotAll: true });
  const { mutate, isLoading } = useRegister();

  const options = [
    { label: t('maktab_oquvchisi'), value: 'PUPIL' },
    { label: t('talaba'), value: 'STUDENT' },
    { label: t('oqituvchi'), value: 'TEACHER' },
    { label: t('boshqa'), value: 'OTHER' }
  ];

  const onFinish = (values: IProfile) => {
    mutate(
      {
        ...values,
        birthday: dayjs(values.birthday).format('YYYY-MM-DD')
      },
      {
        onSuccess: () => {
          dispatch(closeAuthModal());
        }
      }
    );
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-center gap-1.5">
        <AkamLogoCheckIcon aria-label="Akam Logo" />
        <AkamLogoTextIcon aria-label="Akam logo text" />
      </div>
      <h2 className="text-center text-xl font-semibold">{t('profilingizni_toldiring')}</h2>
      <Form
        onFinishFailed={() => message.error(t('iltimos_barcha_maydonlarni_toldiring'))}
        onFinish={onFinish}
        layout="vertical"
        className="!mt-5 !space-y-3.5"
        autoComplete="off"
        requiredMark={false}
        form={form}
      >
        <Row gutter={14}>
          <Col span={12}>
            <label className="text-sm font-medium">{t('auth.first_name_placeholder')}</label>
            <Form.Item
              name="full_name"
              className="!mb-0"
              rules={[
                {
                  required: true,
                  message: ''
                }
              ]}
            >
              <Input className="!h-11 !w-full !rounded-lg !border !border-gray-300 !bg-gray-50 focus:!border-green-500 [&.ant-input-status-error]:!border-red-500" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="text-sm font-medium">{t('auth.last_name_placeholder')}</label>
            <Form.Item
              className="!mb-0"
              name="last_name"
              rules={[
                {
                  required: true,
                  message: ''
                }
              ]}
            >
              <Input className="!h-11 !w-full !rounded-lg !border !border-gray-300 !bg-gray-50 focus:!border-green-500 [&.ant-input-status-error]:!border-red-500" />
            </Form.Item>
          </Col>
        </Row>
        <label className="text-sm font-medium">{t('auth.region_placeholder')}</label>
        <Form.Item
          name="address_id"
          className="!mb-0"
          rules={[
            {
              required: true,
              message: ''
            }
          ]}
        >
          <Select
            className="!mt-1.5 [&.ant-select-status-error_.ant-select-selector]:!border-red-500 [&_.ant-select-selector]:!h-11 [&_.ant-select-selector]:!border [&_.ant-select-selector]:!border-gray-200 [&_.ant-select-selector]:!bg-gray-50"
            options={regions}
            suffixIcon={<ArrowDownIcon />}
            loading={regionsLoading}
            allowClear
          />
        </Form.Item>
        <Row gutter={10} className="!mt-3.5 flex items-center">
          <Col span={12} className="!mt-[0.3rem]">
            <label className="text-sm font-medium">{t('auth.birth_date_placeholder')}</label>
            <Form.Item name="birthday" className="!mb-0 " rules={[{ required: true, message: '' }]}>
              <DatePicker
                size="large"
                className="!h-11 [&.ant-picker-status-error]:!border-red-500 !w-full !rounded-lg !border !border-gray-300 !bg-gray-50 focus:!border-green-500"
                suffixIcon={<CalendarIcon />}
                format={{
                  format: 'DD.MM.YYYY',
                  type: 'mask'
                }}
                placeholder="kk.oo.yyyy"
                renderExtraFooter={() => null}
                disabledDate={current => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="text-sm font-medium">{t('auth.gender_validation_message')}</label>
            <Form.Item
              name="gender"
              className="!mb-0"
              rules={[
                {
                  required: true,
                  message: ''
                }
              ]}
            >
              <Select
                className="!mt-1.5 [&.ant-select-status-error_.ant-select-selector]:!border-red-500 [&_.ant-select-selector]:!h-11 [&_.ant-select-selector]:!border [&_.ant-select-selector]:!border-gray-200 [&_.ant-select-selector]:!bg-gray-50"
                options={[
                  {
                    label: t('auth.gender_male_label'),
                    value: 'erkak'
                  },
                  {
                    label: t('auth.gender_female_label'),
                    value: 'ayol'
                  }
                ]}
                loading={regionsLoading}
                allowClear
                suffixIcon={<ArrowDownIcon />}
              />
            </Form.Item>
          </Col>
        </Row>
        <label className="text-sm font-medium">{t('auth.kim_sifatida')}</label>
        <Form.Item
          name="type"
          rules={[
            {
              required: true,
              message: ''
            }
          ]}
        >
          <Select
            placeholder={t('auth.region_placeholder')}
            className="!mt-1.5 [&.ant-select-status-error_.ant-select-selector]:!border-red-500 [&_.ant-select-selector]:!h-11 [&_.ant-select-selector]:!border [&_.ant-select-selector]:!border-gray-200 [&_.ant-select-selector]:!bg-gray-50"
            options={options}
            loading={regionsLoading}
            suffixIcon={<ArrowDownIcon />}
            allowClear
          />
        </Form.Item>
        <Button
          loading={isLoading}
          htmlType="submit"
          className="!mt-7 !h-11 !w-full !rounded-lg !bg-green-500 !font-semibold !text-white"
        >
          {t('auth.kirish')}
        </Button>
      </Form>
    </>
  );
};

export default RegisterStep;
